import { Dashboard } from './Dashboard.js';
import { Onboarding, Step2 as CatalogReview } from './Onboarding.js';
import { CombinedOrders } from './combined/CombinedOrders.js';
import { CombinedStatements } from './combined/CombinedStatements.js';
import { CombinedPayouts } from './combined/CombinedPayouts.js';
import { CampaignsList } from './campaigns/CampaignsList.js';
import { Insights } from './insights/Insights.js';
import { Notifications } from './notifications/Notifications.js';
import { useScopeContext } from '../lib/scopeContext.js';
import { SURFACE_BY_ID, type SurfaceId } from './nav.js';

/**
 * The routed workspace surface (clarity-navigation-scope task 3.1). Each of the
 * six surfaces (`dashboard`, `orders`, `statements`, `payouts`, `catalog`,
 * `onboarding`) is a child of one of the three scope branches
 * (`all/*`, `brand/:brandId/*`, `store/:storeId/*`), so it renders INSIDE a
 * `<ScopeProvider>` and can read the resolved scope.
 *
 * `dashboard` renders the existing {@link Dashboard} roll-up (which already reads
 * the client-narrowed branches; task 6.3 scopes it to the in-scope branch set).
 * `onboarding` is a BRANCH-ONLY surface (task 5.2): reached only at store scope —
 * the BranchOnlyGuard (3.3) narrows all/brand to a single store and the
 * OnboardingLockGuard (3.4) sits in the store branch — so the scope context
 * resolves to exactly one `store`, and it renders the existing
 * {@link Onboarding} wizard for that scoped store (no `?store=` picker).
 *
 * The flexible data surfaces (task 6.4) route to their scope-aware views: a
 * one-branch scope renders the single-store page unchanged, a multi-branch
 * all/brand scope renders the combined view (orders merged with a Store column;
 * statements/payouts as per-branch sections with a roll-up). Campaigns renders
 * the per-store list for a single branch, or a branch picker otherwise.
 */
export function WorkspaceSurface({ surface }: { surface: SurfaceId }) {
  switch (surface) {
    case 'dashboard':
      return <Dashboard />;
    case 'onboarding':
      return <OnboardingSurface />;
    case 'catalog':
      return <CatalogSurface />;
    case 'orders':
      return <CombinedOrders />;
    case 'statements':
      return <CombinedStatements />;
    case 'payouts':
      return <CombinedPayouts />;
    case 'campaigns':
      return <CampaignsList />;
    case 'insights':
      return <Insights />;
    case 'notifications':
      return <Notifications />;
    default:
      // Exhaustive fallback for a surface added to the registry before its view.
      return <SurfacePlaceholder surface={surface} />;
  }
}

/**
 * The catalog surface (task 5.3, Req 4.1 / 4.5): branch-only, rendered from the
 * scope's SINGLE store. Catalog is reachable only at store scope — the
 * BranchOnlyGuard (3.3) narrows all/brand to one store (and never renders an
 * aggregate catalog, Req 4.5), the OnboardingLockGuard (3.4) keeps a
 * not-yet-onboarded store on onboarding — so `store` is non-null in practice.
 *
 * It REUSES the existing Step-2 catalog-review component (`Step2`, exported from
 * {@link Onboarding} as `CatalogReview`) against the scoped store, with NO
 * catalog math change. Per task 5.3, a full clean extraction of Step 2 out of
 * the wizard is non-trivial (it carries a large web of local draft/confirm/merge
 * state), so we ship the sanctioned thin route: render the existing component
 * directly. Omitting its wizard `onBack`/`onNext` puts it in standalone chrome —
 * no step navigation, the submit reads "Save catalog" — over the same persist
 * path.
 *
 * The container keeps the stable `data-surface="catalog"` + `data-scope` markers
 * the routing tests assert on. A transient null `store` (narrowed list not yet
 * resolved) shows a small loading state rather than crashing.
 */
function CatalogSurface() {
  const { scope, store } = useScopeContext();

  if (!store) {
    return (
      <section data-surface="catalog" data-scope={scope.kind}>
        <p role="status" className="muted">
          Loading catalog…
        </p>
      </section>
    );
  }

  return (
    <section data-surface="catalog" data-scope={scope.kind}>
      <CatalogReview store={store} />
    </section>
  );
}

/**
 * The onboarding surface (task 5.2, Req 4.1 / 4.4): branch-only, rendered from
 * the scope's SINGLE store. Onboarding is reachable only at store scope — the
 * BranchOnlyGuard (3.3) narrows all/brand to one store and the OnboardingLockGuard
 * (3.4) forces a not-yet-onboarded store onto this surface — so `store` is
 * non-null in practice. The wizard (and its resume logic) is reused unchanged;
 * it no longer depends on `StoreWorkspace.StorePicker` / the `?store=` select.
 *
 * The container keeps the stable `data-surface="onboarding"` + `data-scope`
 * markers the routing guards' tests assert on, around the real wizard. A
 * transient null `store` (the narrowed store list not yet resolved) renders a
 * small loading state rather than crashing; a genuinely absent store (which the
 * guards make unreachable) renders a safe fallback.
 */
function OnboardingSurface() {
  const { scope, store } = useScopeContext();

  if (!store) {
    return (
      <section data-surface="onboarding" data-scope={scope.kind}>
        <p role="status" className="muted">
          Loading onboarding…
        </p>
      </section>
    );
  }

  return (
    <section data-surface="onboarding" data-scope={scope.kind}>
      <Onboarding store={store} />
    </section>
  );
}

/**
 * A thin, scope-aware stand-in for the not-yet-wired surfaces. It reads the
 * resolved scope so the placeholder reflects WHICH branches are in scope — proof
 * the surface mounted under a `<ScopeProvider>` — and names the surface so the
 * routing tests can assert the right surface rendered for each URL shape.
 */
function SurfacePlaceholder({ surface }: { surface: SurfaceId }) {
  const { scope, branches, brand, store } = useScopeContext();
  const def = SURFACE_BY_ID[surface];

  const scopeLabel =
    scope.kind === 'all'
      ? 'All stores'
      : scope.kind === 'brand'
        ? (brand?.name ?? 'Brand')
        : (store?.name ?? store?.externalStoreId ?? 'Store');

  return (
    <section data-surface={surface} data-scope={scope.kind}>
      <div className="page-head">
        <div>
          <h2>{def.id.charAt(0).toUpperCase() + def.id.slice(1)}</h2>
          <p className="sub">
            {scopeLabel} · {branches.length} branch{branches.length === 1 ? '' : 'es'}
          </p>
        </div>
      </div>
      <div className="card empty">
        <p className="muted">This page isn’t available yet.</p>
      </div>
    </section>
  );
}
